import { useState, useEffect, useRef } from "react";
import { House } from "lucide-react";
export default function OurSolutionsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);
  const cardStyle = {
    backgroundColor: "rgb(248, 248, 248)",
    border: "1px solid #EBEBEB",
    borderRadius: "0px",
    padding: "32px"
  };
  const iconContainerStyle = {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    backgroundColor: "#F0F0F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  };
  const h3Style = {
    color: "rgb(26, 26, 26)",
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontSize: "20px",
    fontWeight: "400",
    lineHeight: "28px",
    letterSpacing: "-0.5px",
    border: "0px solid rgb(26, 26, 26)"
  };
  const pStyle = {
    color: "oklch(0.551 0.027 264.364)",
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    fontSize: "14px",
    fontWeight: "300",
    lineHeight: "22.75px",
    border: "0px solid oklch(0.551 0.027 264.364)"
  };
  const footerLabelStyle = {
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    fontSize: "12px",
    fontWeight: "400",
    color: "oklch(0.551 0.027 264.364)"
  };
  const featuredLinkStyle = {
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    fontSize: "11px",
    fontWeight: "600",
    letterSpacing: "0.1em",
    color: "#E51F26",
    textDecoration: "none",
    cursor: "pointer"
  };
  const cards = [{
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="5" r="1"></circle><path d="m9 20 3-6 3 6"></path><path d="m6 8 6 2 6-2"></path><path d="M12 10v4"></path></svg>,
    title: "Advanced Bracing & Orthotics",
    body: "Custom AFOs, KAFOs, microprocessor C-Brace, MyoPro upper extremity orthoses, CROW boots, and pediatric bracing — fabricated to your anatomy.",
    footer: "featured"
  }, {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 10.01h.01"></path><path d="M10 14.01h.01"></path><path d="M14 10.01h.01"></path><path d="M14 14.01h.01"></path><path d="M18 6v11.5"></path><path d="M6 6v12"></path><rect x="2" y="6" width="20" height="12" rx="2"></rect></svg>,
    title: "Wound Care",
    body: "Negative pressure wound therapy, amniotic skin substitutes, and advanced dressings for chronic, complex, and non-healing wounds.",
    footer: "standard"
  }, {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path></svg>,
    title: "Lymphedema & Compression",
    body: "AIROS pneumatic compression devices and Aero-Wrap adjustable garments for lymphedema and chronic venous insufficiency.",
    footer: "standard"
  }, {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"></path><path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"></path><path d="M16 17h4"></path><path d="M4 13h4"></path></svg>,
    title: "Diabetic Foot Care",
    body: "Scannable therapeutic footwear, custom inserts, and CROW boots for high-risk diabetic feet — Medicare-covered annually for qualifying patients.",
    footer: "standard"
  }, {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12.8 19.6A2 2 0 1 0 14 16H2"></path><path d="M17.5 8a2.5 2.5 0 1 1 2 4H2"></path><path d="M9.8 4.4A2 2 0 1 1 11 8H2"></path></svg>,
    title: "Respiratory & Post-Op DME",
    body: "House respiratory equipment, CPMs, cold therapy units, and bone growth stimulators supporting recovery and independence.",
    footer: "standard"
  }];
  return <section id="OurSolutions" ref={sectionRef} style={{
    backgroundColor: "rgb(255, 255, 255)",
    backgroundPosition: "0% 0%",
    backgroundRepeat: "repeat",
    color: "rgb(26, 26, 26)",
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    animation: "none 0s ease 0s 1 normal none running"
  }} className="w-full">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Inter:wght@300;400;500;600&display=swap');`}</style>
      <div className="md:py-0 py-0" style={{
      paddingTop: "clamp(96px, 10vw, 128px)",
      paddingBottom: "clamp(96px, 10vw, 128px)"
    }}>
        <div className="page-container">
          <div className={`flex flex-col items-center text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <span style={{
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            fontSize: "12px",
            fontWeight: "600",
            letterSpacing: "0.15em",
            color: "#E51F26",
            textTransform: "uppercase"
          }}>
              OUR SOLUTIONS
            </span>
            <h2 style={{
            color: "rgb(26, 26, 26)",
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: "36px",
            fontWeight: "500",
            lineHeight: "45px",
            letterSpacing: "-0.9px",
            border: "0px solid rgb(26, 26, 26)",
            textAlign: "center",
            animation: "none 0s ease 0s 1 normal none running",
            marginTop: "12px",
            marginBottom: "16px"
          }}>
              Clinically validated medical products.
            </h2>
            <div style={{
            width: "40px",
            height: "2px",
            backgroundColor: "#E51F26"
          }} />
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: "150ms"
        }}>
            {cards.map((card, index) => <div key={index} data-index={index} style={{
            ...cardStyle,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            transitionDelay: `${150 + index * 80}ms`
          }} className="transition-shadow duration-300 hover:shadow-lg">
                <div style={iconContainerStyle}>
                  <span style={{
                color: "#E51F26",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                    {card.icon}
                  </span>
                </div>
                <div style={{
              height: "24px"
            }} />
                <h3 style={h3Style}>{card.title}</h3>
                <div style={{
              height: "8px"
            }} />
                <p style={pStyle}>{card.body}</p>
                <div style={{
              flexGrow: 1,
              minHeight: "16px"
            }} />
                {card.footer === "featured" ? <span style={featuredLinkStyle} className="cursor-pointer hover:opacity-80 transition-opacity duration-150">
                    FEATURED TECHNOLOGY
                  </span> : <span style={footerLabelStyle}>
                    Industry standard support
                  </span>}
              </div>)}
            <div className="hidden md:block" />
          </div>
        </div>
      </div>
    </section>;
}